import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { socketClient } from '../lib/socket';
import { api } from '../lib/api';
import { BottomNav } from '../ui-kit/Layouts';
import Icon from '../ui-kit/Icon';
import { Button } from '../ui-kit/Button';
import { WinModal, GlobalWinToast } from '../ui-kit/Feedback';
import { imgTrophy } from '../assets';
import { useTranslation } from 'react-i18next';

interface TournamentInfo {
    id: string;
    title: string;
    startTime: string;
    prizePool: number;
    prizeUnit: string;
    ticketPrice: number;
    maxBalls: number;
    cardFormat: string;
    status: string;
    gameSessionId: string | null;
    userTicketCount: number;
    userTickets: Array<{
        id: string;
        grid: (number | null)[][];
        hits: number[];
        status: string;
    }>;
}

type ViewType = 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile';

interface SundayGalaPageProps {
    onNavigate: (view: ViewType) => void;
}

function formatCountdown(totalSeconds: number) {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return { days, hours, minutes, seconds };
}

// ═══════════════════════════════════════════════════════════
// PREMIUM 2.5D GAME ROOM (CSS + Framer Motion)
// ═══════════════════════════════════════════════════════════

function TournamentLiveRoom({
    tournament,
    onBack,
}: {
    tournament: TournamentInfo;
    onBack: () => void;
}) {
    const { t } = useTranslation();
    const [drawnNumbers, setDrawnNumbers] = useState<number[]>([]);
    const [lastBall, setLastBall] = useState<number | null>(null);
    const [tickets, setTickets] = useState(tournament.userTickets || []);
    const [showWinModal, setShowWinModal] = useState(false);
    const [winAmount, setWinAmount] = useState(0);
    const [winType, setWinType] = useState('');
    const [gameEnded, setGameEnded] = useState(false);
    const [globalToast, setGlobalToast] = useState({ message: '', prize: 0, isVisible: false, winnerName: '' });
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const pollGameState = useCallback(async () => {
        try {
            const state = await api.getGameState();
            if (state.ok && state.state) {
                const s: any = state.state;
                if (s.sundayDrawnNumbers && s.sundayDrawnNumbers.length > 0) {
                    setDrawnNumbers(s.sundayDrawnNumbers);
                    setLastBall(s.sundayDrawnNumbers[s.sundayDrawnNumbers.length - 1]!);
                }
                if (s.sundayStatus === 'FINISHED' || s.sundayStatus === null) {
                    setGameEnded(true);
                }
            }

            // Refresh user tickets
            if (tournament.id) {
                // Instead of getSundayInfo, just re-fetch tournaments to get updated hits
                const res = await api.getTournaments();
                if (res.ok) {
                    const match = res.tournaments.find(t => t.id === tournament.id);
                    if (match && match.userTickets) {
                        setTickets(match.userTickets);
                    }
                }
            }
        } catch { /* ignore */ }
    }, [tournament.id]);

    useEffect(() => {
        pollGameState();
        pollRef.current = setInterval(pollGameState, 3000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [pollGameState]);

    useEffect(() => {
        const handleWin = (payload: any) => {
            const userId = String(useAuthStore.getState().user?.id);
            const wUserId = String(payload.userId);
            const isMyWin = wUserId === userId;
            const wName = payload.username || payload.displayName || 'Alguien';
            
            if (isMyWin) {
                setWinType(payload.winType || 'BINGO');
                setWinAmount(payload.prize || 0);
                setShowWinModal(true);
            } else {
                setGlobalToast({
                    message: `🎉 ${wName} won ${payload.winType || 'BINGO'}!`,
                    prize: payload.prize || 0,
                    isVisible: true,
                    winnerName: wName
                });
                setTimeout(() => setGlobalToast(g => ({ ...g, isVisible: false })), 5000);
            }
        };

        const handleGameEnded = () => setGameEnded(true);

        // Ensure socket is explicitly connected for the room
        const socket = socketClient.connect();
        
        // Wait for connection to emit join
        const onConnect = () => {
            if (tournament.gameSessionId) {
                socket.emit('join_room', { sessionId: tournament.gameSessionId, userId: String(useAuthStore.getState().user?.id) }, (res) => {
                    console.log('Joined Sunday Gala room', res);
                });
            }
        };

        if (socket.connected) {
            onConnect();
        } else {
            socket.on('connect', onConnect);
        }

        socket.on('win', handleWin);
        socket.on('game_ended', handleGameEnded);
        
        return () => {
            socket.off('connect', onConnect);
            socket.off('win', handleWin);
            socket.off('game_ended', handleGameEnded);
            
            if (tournament.gameSessionId) {
                socket.emit('leave_room', { sessionId: tournament.gameSessionId }, () => {});
            }
            socketClient.disconnect();
        };
    }, [t, tournament.gameSessionId]);

    useEffect(() => {
        if (!gameEnded) return;
        const timer = setTimeout(() => {
            onBack();
        }, 10000);
        return () => clearTimeout(timer);
    }, [gameEnded, onBack]);

    const sortedTicketsWithMetrics = useMemo(() => {
        return tickets.map(ticket => {
            const grid = Array.isArray(ticket.grid) ? ticket.grid : [];
            const ticketHits = Array.isArray(ticket.hits) ? ticket.hits : [];
            const hitSet = new Set([...ticketHits, ...drawnNumbers.filter(n => {
                for (const row of grid) {
                    if (Array.isArray(row) && row.includes(n)) return true;
                }
                return false;
            })]);

            let totalNumbers = 0;
            let hitCount = 0;
            
            grid.forEach((row, r) => {
                if (Array.isArray(row)) {
                    row.forEach((cell, c) => {
                        const isCenter = r === 2 && c === 2 && (cell === null || cell === 0) && tournament.cardFormat === '5x5';
                        if (!isCenter && cell !== null && cell !== 0) {
                            totalNumbers++;
                            if (hitSet.has(cell)) hitCount++;
                        }
                    });
                }
            });
            
            return {
                ticket,
                grid,
                hitSet,
                missing: totalNumbers - hitCount
            };
        }).sort((a, b) => a.missing - b.missing);
    }, [tickets, drawnNumbers, tournament.cardFormat]);

    return (
        <div className="fixed inset-0 z-[100] bg-gradient-to-b from-[#0B0F19] to-[#1A1025] flex flex-col overflow-hidden perspective-1000">
            {/* Background Effects */}
            <div className="absolute top-0 inset-x-0 h-64 bg-gradient-to-b from-blue-900/40 via-purple-900/20 to-transparent pointer-events-none" />
            <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-blue-600/20 blur-[120px] pointer-events-none" />
            <div className="absolute top-[10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-yellow-500/10 blur-[100px] pointer-events-none" />

            {/* Header */}
            <header className="px-4 py-4 flex items-center justify-between z-10 bg-black/20 backdrop-blur-md border-b border-white/10 shadow-lg">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 shadow-inner">
                        <Icon name="arrow_back" className="text-white" />
                    </button>
                    <div>
                        <h1 className="text-lg font-display font-black text-white truncate drop-shadow-md">{tournament.title}</h1>
                        <div className="flex items-center gap-2">
                            <motion.span
                                animate={{ opacity: [1, 0.3, 1] }}
                                transition={{ repeat: Infinity, duration: 1.2 }}
                                className="w-2 h-2 rounded-full bg-red-500 inline-block shadow-[0_0_10px_rgba(239,68,68,0.8)]"
                            />
                            <span className="text-xs text-red-500 font-bold tracking-wider">LIVE NOW</span>
                        </div>
                    </div>
                </div>
                <div className="text-right px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-xl shadow-[0_0_15px_rgba(255,193,7,0.15)]">
                    <div className="text-xs text-yellow-500/80 font-bold uppercase">Prize Pool</div>
                    <div className="text-base font-black text-[#FFC107] font-mono leading-tight">{tournament.prizePool.toLocaleString()} {tournament.prizeUnit || 'chips'}</div>
                </div>
            </header>

            {/* 2.5D Ball Display Area */}
            <div className="relative px-4 py-8 flex flex-col items-center justify-center min-h-[160px] z-10">
                <AnimatePresence mode="popLayout">
                    {lastBall !== null ? (
                        <motion.div
                            key={lastBall}
                            initial={{ y: -100, scale: 0.5, rotateX: 90, opacity: 0 }}
                            animate={{ y: 0, scale: 1, rotateX: 0, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0, filter: 'blur(10px)' }}
                            transition={{ type: 'spring', damping: 12, stiffness: 100 }}
                            className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-[0_20px_40px_rgba(0,0,0,0.5),inset_0_-10px_20px_rgba(0,0,0,0.5),inset_0_10px_20px_rgba(255,255,255,0.4)] bg-gradient-to-br from-[#FFD54F] to-[#FF8F00] transform-style-3d border-[3px] border-yellow-300"
                        >
                            {/* Inner glow/reflection to sell the 3D sphere look */}
                            <div className="absolute top-2 left-4 w-8 h-4 bg-white/60 rounded-full blur-[2px] transform -rotate-12" />
                            <span className="text-5xl font-black text-[#663C00] drop-shadow-md font-display transform translate-z-10">{lastBall}</span>
                        </motion.div>
                    ) : (
                        <div className="w-32 h-32 flex items-center justify-center text-white/20 font-bold text-center">
                            Waiting for<br />first ball...
                        </div>
                    )}
                </AnimatePresence>

                {/* Progress bar */}
                <div className="w-full max-w-xs mt-6">
                    <div className="flex justify-between text-[11px] font-bold text-white/50 mb-1.5 uppercase tracking-wide">
                        <span>{t('sunday.drawn')}: {drawnNumbers.length}</span>
                        <span>{t('sunday.total')}: {tournament.maxBalls}</span>
                    </div>
                    <div className="h-1.5 bg-black/40 rounded-full overflow-hidden shadow-inner border border-white/5">
                        <motion.div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-[0_0_10px_rgba(168,85,247,0.5)]"
                            animate={{ width: `${(drawnNumbers.length / tournament.maxBalls) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* History Ribbon */}
            <div className="px-4 py-3 bg-black/30 border-y border-white/5 backdrop-blur-sm z-10 shadow-inner flex gap-2 overflow-x-auto hide-scrollbar whitespace-nowrap mask-edges-x">
                {drawnNumbers.slice().reverse().map((n, i) => (
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        key={i}
                        className={`inline-flex shrink-0 w-10 h-10 rounded-full items-center justify-center text-sm font-black shadow-lg ${i === 0
                            ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black border border-yellow-300'
                            : 'bg-gradient-to-br from-white/10 to-white/5 text-white/80 border border-white/10'
                            }`}
                    >
                        {n}
                    </motion.div>
                ))}
            </div>

            {/* Tickets Grid (Glassmorphism) */}
            <div className="flex-1 overflow-y-auto px-4 pt-6 pb-24 z-10 hide-scrollbar">
                {sortedTicketsWithMetrics.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center opacity-50">
                        <Icon name="confirmation_number" className="text-6xl mb-4" />
                        <p className="font-bold">{t('tournaments.noTicketsBought', 'No tickets bought')}</p>
                    </div>
                ) : (
                    <div className={sortedTicketsWithMetrics.length > 1 ? "grid grid-cols-2 gap-3" : "space-y-6"}>
                        {sortedTicketsWithMetrics.map(({ ticket, grid, hitSet, missing }, idx) => {
                            return (
                                <motion.div
                                    key={ticket.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="relative bg-gradient-to-b from-white/10 to-transparent border border-white/20 rounded-3xl p-4 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] backdrop-blur-xl overflow-hidden"
                                >
                                    {/* Missing Badge */}
                                    {missing > 0 && missing <= 5 && (
                                        <div className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-black px-2 py-0.5 rounded-bl-lg shadow-md z-20 animate-pulse">
                                            {missing === 1 ? '1 to go!' : `${missing} missing`}
                                        </div>
                                    )}
                                    {missing === 0 && (
                                        <div className="absolute top-0 right-0 bg-green-500 text-white text-[9px] font-black px-2 py-0.5 rounded-bl-lg shadow-md z-20">
                                            BINGO!
                                        </div>
                                    )}

                                    {/* Ticket Header */}
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="text-xs uppercase tracking-widest text-[#FFC107] font-black drop-shadow-md">Ticket {idx + 1}</div>
                                        <div className="text-[10px] text-white/30 font-mono tracking-widest">{ticket.id.slice(0, 8)}</div>
                                    </div>

                                    {/* Grid */}
                                    {/* BINGO letters */}
                                    <div className="grid grid-cols-5 gap-1.5 mb-1.5 px-1">
                                        {['B', 'I', 'N', 'G', 'O'].map(letter => (
                                            <div key={letter} className="text-center font-black text-white/40 text-xs">{letter}</div>
                                        ))}
                                    </div>

                                    <div className="grid grid-cols-5 gap-1.5">
                                        {grid.map((row, r) =>
                                            Array.isArray(row) && row.map((cell, c) => {
                                                const isCenter = r === 2 && c === 2 && (cell === null || cell === 0) && tournament.cardFormat === '5x5';
                                                const isHit = cell !== null && cell !== 0 && hitSet.has(cell);

                                                return (
                                                    <div
                                                        key={`${r}-${c}`}
                                                        className={`
                                                            aspect-square ${sortedTicketsWithMetrics.length > 1 ? 'rounded-md text-[10px]' : 'rounded-xl text-sm'} flex items-center justify-center font-black transition-all duration-300
                                                            ${isCenter
                                                                ? 'bg-gradient-to-br from-yellow-400/20 to-yellow-600/10 text-yellow-400 border border-yellow-500/30'
                                                                : isHit
                                                                    ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white border border-white/20 shadow-[0_0_15px_rgba(168,85,247,0.5)] scale-105'
                                                                    : 'bg-black/40 text-white/70 border border-white/5 shadow-inner'
                                                            }
                                                        `}
                                                    >
                                                        {isCenter ? '★' : cell || ''}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* End Game Overlay */}
            <AnimatePresence>
                {gameEnded && !showWinModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
                    >
                        <motion.div
                            initial={{ scale: 0.8, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            className="bg-gradient-to-b from-surface to-background border border-white/20 rounded-3xl p-8 text-center w-full max-w-sm shadow-2xl"
                        >
                            <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-6 border border-red-500/50 shadow-[0_0_30px_rgba(239,68,68,0.3)]">
                                <span className="text-4xl">😔</span>
                            </div>
                            <h2 className="text-2xl font-display font-black text-white mb-2 uppercase">
                                {t('sunday.someoneElseWon', '¡Alguien más ganó!')}
                            </h2>
                            <p className="text-white/50 mb-8 font-medium">
                                {globalToast.winnerName 
                                    ? t('sunday.winnerWas', 'El ganador fue {{name}}', { name: globalToast.winnerName }) 
                                    : t('sunday.betterLuck', 'Mejor suerte para la próxima.')}
                            </p>
                            <Button onClick={onBack} fullWidth size="lg" className="bg-white/10 text-white border border-white/20">{t('sunday.exit', 'Salir')}</Button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <WinModal isOpen={showWinModal} title={winType} amount={winAmount} onClose={() => { setShowWinModal(false); onBack(); }} onClaim={() => { setShowWinModal(false); onBack(); }} />
            <GlobalWinToast
                message={globalToast.message}
                prize={globalToast.prize}
                isVisible={globalToast.isVisible}
            />
        </div>
    );
}


// ═══════════════════════════════════════════════════════════
// TOURNAMENT LOBBY DETAIL VIEW
// ═══════════════════════════════════════════════════════════

function TournamentLobby({
    tournament,
    now,
    chips,
    buyingCard,
    onBack,
    onBuyCard,
    onEnterRoom,
    onNavigate,
}: {
    tournament: TournamentInfo;
    now: number;
    chips: number;
    buyingCard: string | null;
    onBack: () => void;
    onBuyCard: (id: string, price: number) => void;
    onEnterRoom: () => void;
    onNavigate: (view: ViewType) => void;
}) {
    const { t } = useTranslation();
    const startMs = new Date(tournament.startTime).getTime();
    const secsUntilStart = Math.max(0, Math.floor((startMs - now) / 1000));
    const countdown = formatCountdown(secsUntilStart);
    const isLive = tournament.status === 'LIVE' || secsUntilStart === 0;
    const cardPrice = tournament.ticketPrice || 0;
    const isBuyingThis = buyingCard === tournament.id;
    const canBuyCard = chips >= cardPrice && !isBuyingThis && tournament.status === 'SCHEDULED';
    const myTickets = Array.isArray(tournament.userTickets) ? tournament.userTickets : [];
    const [expandedTicket, setExpandedTicket] = useState<string | null>(null);

    return (
        <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            className="pb-32"
        >
            {/* Nav Header */}
            <div className="flex items-center gap-3 mb-6">
                <button onClick={onBack} className="p-2 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                    <Icon name="arrow_back" className="text-white" />
                </button>
                <h2 className="text-xl font-display font-black text-white">{tournament.title}</h2>
            </div>

            {/* Trophy & Prize */}
            <div className="text-center mb-8 isolate">
                <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    className="w-32 h-32 mx-auto mb-[-40px] relative z-10"
                >
                    <img src={imgTrophy} alt="Trophy" className="w-full h-full object-contain drop-shadow-[0_20px_30px_rgba(255,193,7,0.3)]" />
                </motion.div>

                <div className="bg-gradient-to-r from-[#1A1A2E] via-[#2A1B38] to-[#1A1A2E] pt-14 pb-6 px-6 rounded-3xl border border-white/10 shadow-xl relative z-0">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Prize Pool</p>
                    <div className="text-4xl font-black text-[#FFC107] drop-shadow-md">
                        {tournament.prizePool > 0 ? `${tournament.prizePool.toLocaleString()} ${tournament.prizeUnit || 'chips'}` : 'TBA'}
                    </div>
                </div>
            </div>

            {/* Status / Countdown */}
            <div className="mb-8">
                {isLive ? (
                    <div className="text-center">
                        <button
                            onClick={onEnterRoom}
                            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white font-black text-lg py-4 px-8 rounded-2xl shadow-[0_10px_30px_rgba(34,197,94,0.4)] hover:scale-105 transition-transform flex flex-col items-center mx-auto"
                        >
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
                                MATCH IS LIVE
                            </span>
                            <span className="text-xs font-bold text-white/70 uppercase tracking-widest mt-1">Enter Room Now</span>
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-4 gap-3 bg-white/5 p-4 rounded-3xl border border-white/5">
                        {[
                            { value: countdown.days, label: 'Days' },
                            { value: countdown.hours, label: 'Hours' },
                            { value: countdown.minutes, label: 'Min' },
                            { value: countdown.seconds, label: 'Sec' },
                        ].map((item) => (
                            <div key={item.label} className="text-center">
                                <div className="text-2xl font-black text-white font-mono bg-black/20 rounded-xl py-2 mb-1 shadow-inner">
                                    {String(item.value).padStart(2, '0')}
                                </div>
                                <div className="text-white/40 text-[9px] font-bold uppercase tracking-wider">
                                    {item.label}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Buy Tickets Area */}
            {tournament.status === 'SCHEDULED' && (
                <div className="mb-8 p-1 rounded-3xl bg-gradient-to-b from-white/10 to-transparent">
                    <div className="bg-[#1A1525] rounded-[22px] p-5">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h3 className="font-bold text-white">{t('sunday.tickets', 'Cartones')}</h3>
                                <p className="text-white/40 text-xs">{t('sunday.buyMultiple', 'Compra varios para más probabilidades')}</p>
                            </div>
                            <div className="flex items-center gap-1.5 bg-yellow-500/10 px-3 py-1.5 rounded-xl border border-yellow-500/20">
                                <Icon name="toll" className="text-yellow-500 text-sm" />
                                <span className="text-yellow-500 font-bold">{cardPrice}</span>
                            </div>
                        </div>

                        <Button
                            onClick={() => onBuyCard(tournament.id, cardPrice)}
                            disabled={!canBuyCard && chips >= cardPrice} // let them click if no chips to show store hint
                            fullWidth
                            size="lg"
                            className="shadow-lg"
                        >
                            {isBuyingThis ? t('sunday.purchasing', 'Comprando...') : t('sunday.buyTicketPrice', 'Comprar Cartón ({{price}})', { price: cardPrice })}
                        </Button>

                        {chips < cardPrice && (
                            <div className="mt-4 text-center">
                                <p className="text-red-400 text-xs mb-2">Insufficient chips. You have {chips}.</p>
                                <button
                                    onClick={() => onNavigate('store')}
                                    className="text-sm font-bold text-primary hover:underline"
                                >
                                    Go to Store
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* My Tickets List */}
            {myTickets.length > 0 && (
                <div>
                    <h3 className="text-white font-bold opacity-80 mb-4 px-2 uppercase tracking-wide text-sm">
                        My Tickets ({myTickets.length})
                    </h3>
                    <div className="space-y-3">
                        {myTickets.map((ticket, idx) => {
                            const grid = Array.isArray(ticket.grid) ? ticket.grid : [];
                            const isExpanded = expandedTicket === ticket.id;

                            return (
                                <div key={ticket.id} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                                    <button
                                        onClick={() => setExpandedTicket(isExpanded ? null : ticket.id)}
                                        className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-black text-xs">
                                                {idx + 1}
                                            </div>
                                            <div className="text-left">
                                                <div className="text-white font-bold text-sm">{t('tournaments.ticket', 'Ticket')}</div>
                                                <div className="text-white/30 text-[10px] uppercase">{tournament.cardFormat}</div>
                                            </div>
                                        </div>
                                        <Icon name={isExpanded ? 'expand_less' : 'expand_more'} className="text-white/40" />
                                    </button>

                                    <AnimatePresence>
                                        {isExpanded && grid.length > 0 && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="px-4 pb-4"
                                            >
                                                <div className="grid grid-cols-5 gap-1 pt-2">
                                                    {['B', 'I', 'N', 'G', 'O'].map(letter => (
                                                        <div key={letter} className="text-center font-black text-white/30 text-[10px]">{letter}</div>
                                                    ))}
                                                    {grid.map((row: any, r: number) =>
                                                        Array.isArray(row) && row.map((cell: any, c: number) => {
                                                            const isCenter = r === 2 && c === 2 && cell === null;
                                                            return (
                                                                <div key={`${r}-${c}`} className={`aspect-square rounded-md flex items-center justify-center text-xs font-bold ${isCenter ? 'bg-yellow-500/20 text-yellow-500' : 'bg-black/30 text-white/50 border border-white/5'}`}>
                                                                    {isCenter ? '★' : cell}
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </motion.div>
    );
}

// ═══════════════════════════════════════════════════════════
// MASTER VIEW (Tournament List)
// ═══════════════════════════════════════════════════════════

export function SundayGalaPage({ onNavigate }: SundayGalaPageProps) {
    const { t } = useTranslation();
    const chips = useUserStore((s) => s.chips);
    const refreshBalance = useUserStore((s) => s.refreshBalance);
    const [tournaments, setTournaments] = useState<TournamentInfo[]>([]);
    const [loading, setLoading] = useState(true);

    // Master-Detail State
    const [selectedTournament, setSelectedTournament] = useState<TournamentInfo | null>(null);
    const [showGameRoom, setShowGameRoom] = useState(false);

    const [buyingCard, setBuyingCard] = useState<string | null>(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const fetchTournaments = useCallback(async () => {
        try {
            const res = await api.getTournaments();
            if (res.ok) {
                setTournaments(res.tournaments);
                // Update selected tournament safely without closure staleness
                setSelectedTournament((prev) => {
                    if (prev) {
                        const match = res.tournaments.find((t: any) => t.id === prev.id);
                        return match || null;
                    }
                    return null;
                });
            }
        } catch (err) { }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchTournaments();
        const interval = setInterval(fetchTournaments, 10000);
        return () => clearInterval(interval);
    }, [fetchTournaments]);

    const handleNavAction = (tab: string) => {
        if (tab === 'sunday') {
            // Reset to master view if they click tab again
            setSelectedTournament(null);
            setShowGameRoom(false);
            return;
        }
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        onNavigate(validViews.includes(tab as any) ? (tab as any) : 'home');
    };

    const handleBuyCard = async (tournamentId: string, price: number) => {
        if (chips < price || buyingCard) return;
        try {
            setBuyingCard(tournamentId);
            const res = await api.buyTournamentTicket(tournamentId);
            if (res.ok) {
                await fetchTournaments();
                await refreshBalance();
            }
        } catch (err: any) {
            console.error('Failed to buy tournament ticket:', err);
        } finally {
            setBuyingCard(null);
        }
    };

    // Render Game Room View
    if (showGameRoom && selectedTournament) {
        return <TournamentLiveRoom tournament={selectedTournament} onBack={() => { setShowGameRoom(false); fetchTournaments(); }} />;
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Header (Hidden in inner views since they have their own) */}
            {!selectedTournament && (
                <header className="px-4 pt-8 pb-4">
                    <div className="text-left">
                        <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">BingoTon</span>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight font-display">Tournaments</h1>
                    </div>
                </header>
            )}

            <main className="pt-4 px-4 max-w-lg mx-auto">
                <AnimatePresence mode="wait">
                    {/* Render Detail View */}
                    {selectedTournament ? (
                        <TournamentLobby
                            key="detail"
                            tournament={selectedTournament}
                            now={now}
                            chips={chips}
                            buyingCard={buyingCard}
                            onBuyCard={handleBuyCard}
                            onEnterRoom={() => setShowGameRoom(true)}
                            onBack={() => setSelectedTournament(null)}
                            onNavigate={onNavigate}
                        />
                    ) : (
                        /* Render Master View */
                        <motion.div
                            key="master"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-4 pb-32"
                        >
                            {loading ? (
                                <div className="py-20 text-center opacity-50">{t('sunday.loading', 'Cargando...')}</div>
                            ) : tournaments.length === 0 ? (
                                <div className="py-20 text-center border border-white/5 rounded-3xl bg-white/5">
                                    <Icon name="event_busy" className="text-5xl opacity-40 mb-3 block" />
                                    <p className="font-bold text-white/50">{t('sunday.noTournaments')}</p>
                                </div>
                            ) : (
                                tournaments.map((tr) => {
                                    const startMs = new Date(tr.startTime).getTime();
                                    const isLive = tr.status === 'LIVE' || (startMs - now <= 0);
                                    const myTicketsCount = tr.userTickets?.length || 0;

                                    return (
                                        <button
                                            key={tr.id}
                                            onClick={() => setSelectedTournament(tr)}
                                            className="w-full text-left bg-surface border border-white/5 p-5 rounded-3xl hover:bg-white/5 transition-colors relative overflow-hidden group"
                                        >
                                            {/* Glow effect on hover */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                            <div className="relative z-10 flex items-start justify-between mb-4">
                                                <div>
                                                    <h3 className="text-lg font-bold text-white mb-1">{tr.title}</h3>
                                                    {isLive ? (
                                                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wider">
                                                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                                            {t('sunday.live')}
                                                        </span>
                                                    ) : (
                                                        <span className="text-white/40 text-xs font-medium">{t('sunday.startsAt', 'Comienza')}: {new Date(tr.startTime).toLocaleString()}</span>
                                                    )}
                                                </div>
                                                <div className="text-right flex flex-col items-end">
                                                    <span className="text-[10px] uppercase text-yellow-500/60 font-bold tracking-widest leading-none mb-1">{t('sunday.prize')}</span>
                                                    <span className="text-xl font-black text-yellow-500 leading-none">{tr.prizePool} {tr.prizeUnit || 'chips'}</span>
                                                </div>
                                            </div>

                                            <div className="relative z-10 flex items-center justify-between border-t border-white/5 pt-3">
                                                <div className="flex items-center gap-2">
                                                    <Icon name="confirmation_number" className={myTicketsCount > 0 ? 'text-primary' : 'text-white/20'} />
                                                    <span className={`text-sm font-bold ${myTicketsCount > 0 ? 'text-white' : 'text-white/40'}`}>
                                                        {myTicketsCount} {t('sunday.tickets', 'Cartones')}
                                                    </span>
                                                </div>
                                                <Icon name="chevron_right" className="text-white/30" />
                                            </div>
                                        </button>
                                    );
                                })
                            )}

                            {/* "How it works" Info Card at the bottom of the list */}
                            <div className="bg-white/5 border border-white/5 rounded-3xl p-6 mt-8">
                                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                                    <Icon name="info" className="text-primary" />
                                    {t('sunday.howItWorks', 'Cómo funciona')}
                                </h3>
                                <ul className="space-y-3 text-white/50 text-sm">
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">1</div>
                                        <span>{t('sunday.step1', 'Compra todos los cartones que quieras para cualquier torneo programado.')}</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">2</div>
                                        <span>{t('sunday.step2', 'Entra al Lobby del Torneo cuando termine la cuenta regresiva.')}</span>
                                    </li>
                                    <li className="flex gap-3">
                                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold shrink-0">3</div>
                                        <span>{t('sunday.step3', '¡Marca líneas o cartón completo para reclamar tu parte del Pozo de Premios!')}</span>
                                    </li>
                                </ul>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            <BottomNav activeTab="sunday" onTabChange={handleNavAction} />
        </div>
    );
}
